<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.add_employee") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #26a1d6">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Avatar Upload Input -->
          <base-image-upload-input
            col="6"
            identifier="avatar"
            :placeholder="$t('PLACEHOLDERS.profileImage')"
            @selectImage="selectAvatarImage"
          />
          <!-- End:: Avatar Upload Input -->

          <!-- Start:: ID Image Upload Input -->
          <base-image-upload-input
            col="6"
            identifier="id_image"
            :placeholder="$t('PLACEHOLDERS.idImage')"
            @selectImage="selectImage"
            required
          />
          <!-- End:: ID Image Upload Input -->

          <!-- Start:: Employee Name Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.employeeName')"
            v-model.trim="data.name"
            :maxlength="50"
            required
          />
          <!-- End:: Employee Name Input -->

          <!-- Start:: Job Select -->
          <base-select-input
            col="6"
            :optionsList="activeJobs"
            :placeholder="$t('PLACEHOLDERS.selectJob')"
            v-model="data.job"
            required
          />
          <!-- End:: Job Select -->

          <!-- Start:: Bank Select -->
          <base-select-input
            col="6"
            :optionsList="activeBanks"
            :placeholder="$t('PLACEHOLDERS.bankName')"
            v-model="data.bank"
            required
          />
          <!-- End:: Bank Select -->

          <!-- Start:: mobile Input -->
          <base-phone-input
            col="6"
            required
            v-model="data.mobile"
            @dialCode="dialCode"
            @isoCode="isoCode"
            :placeholder="$t('PLACEHOLDERS.phone')"
            :defaultCountry="data.iso_code"
          />
          <!-- End:: mobile Input -->

          <!-- Start:: ID Number Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.idNumber')"
            v-model.trim="data.id_number"
            :maxlength="10"
            required
            @input="validateIdNumber"
          />
          <!-- End:: ID Number Input -->

          <!-- Start:: Birth Date Input -->
          <base-input
            col="6"
            type="date"
            :placeholder="$t('PLACEHOLDERS.birthDate')"
            v-model="data.birth_date"
            required
          />
          <!-- End:: Birth Date Input -->

          <!-- Start:: Age Input (Read Only) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.employeeAge')"
            v-model.trim="data.age"
            disabled
          />
          <!-- End:: Age Input (Read Only) -->

          <!-- Start:: Diseases Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.diseases')"
            v-model.trim="data.diseases"
            :maxlength="50"
            required
            @input="sanitizeAlphaNumericField('diseases')"
          />
          <!-- End:: Diseases Input -->

          <!-- Start:: Medications Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.medications')"
            v-model.trim="data.medications"
            :maxlength="50"
            required
            @input="sanitizeAlphaNumericField('medications')"
          />
          <!-- End:: Medications Input -->

          <!-- Start:: Blood Type Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.bloodType')"
            v-model.trim="data.blood_type"
            :maxlength="50"
            required
            @input="sanitizeAlphaNumericField('blood_type')"
          />
          <!-- End:: Blood Type Input -->

          <!-- Start:: Weight Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.weight')"
            v-model.trim="data.weight"
            :maxlength="50"
            required
            @input="sanitizeNumericField('weight')"
          />
          <!-- End:: Weight Input -->

          <!-- Start:: Height Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.height')"
            v-model.trim="data.height"
            :maxlength="50"
            required
            @input="sanitizeNumericField('height')"
          />
          <!-- End:: Height Input -->

          <!-- Start:: Account Number Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.accountNumber')"
            v-model.trim="data.account_number"
            :maxlength="50"
            required
            @input="sanitizeAlphaNumericField('account_number')"
          />
          <!-- End:: Account Number Input -->

          <!-- Start:: IBAN Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.ibanNumber')"
            v-model.trim="data.iban"
            :maxlength="50"
            required
            @input="sanitizeAlphaNumericField('iban')"
          />
          <!-- End:: IBAN Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import BasePhoneInput from "@/components/formInputs/BasePhoneInput.vue";

export default {
  name: "CreateEmployee",
  components: {
    BasePhoneInput,
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        avatar: {
          path: null,
          file: null,
        },
        id_image: {
          path: null,
          file: null,
        },
        name: null,
        job: null,
        bank: null,
        mobile: null,
        dial_code: null,
        iso_code: null,
        id_number: null,
        birth_date: null,
        age: null,
        diseases: null,
        medications: null,
        blood_type: null,
        weight: null,
        height: null,
        account_number: null,
        iban: null,
      },
      activeJobs: [],
      activeBanks: [],
      // End:: Data Collection To Send
    };
  },

  watch: {
    "data.birth_date": {
      handler(newValue) {
        this.calculateAge();
      },
      immediate: false,
    },
  },

  methods: {
    isoCode(iosCode) {
      this.data.iso_code = iosCode;
    },
    dialCode(dialCodeValue) {
      this.data.dial_code = dialCodeValue;
    },
    selectImage(selectedImage) {
      this.data.id_image = selectedImage;
    },
    selectAvatarImage(selectedImage) {
      this.data.avatar = selectedImage;
    },

    validateIdNumber() {
      // Only allow numbers
      this.data.id_number = this.data.id_number.replace(/\D/g, "");

      // Limit to 10 digits
      if (this.data.id_number.length > 10) {
        this.data.id_number = this.data.id_number.substring(0, 10);
      }
    },

    sanitizeAlphaNumericField(fieldName) {
      const fieldValue = this.data[fieldName] || "";
      this.data[fieldName] = fieldValue
        .replace(/[^A-Za-z0-9\u0600-\u06FF\s]/g, "")
        .substring(0, 50);
    },

    sanitizeNumericField(fieldName) {
      const fieldValue = this.data[fieldName] || "";
      this.data[fieldName] = fieldValue.replace(/\D/g, "").substring(0, 50);
    },

    validateMedicalInputs() {
      if (
        !this.data.diseases?.trim() ||
        !this.data.medications?.trim() ||
        !this.data.blood_type?.trim() ||
        !this.data.weight?.trim() ||
        !this.data.height?.trim()
      ) {
        this.$message.error(this.$t("MESSAGES.medicalFieldsRequired"));
        return false;
      }

      const alphaNumericRegex = /^[A-Za-z0-9\u0600-\u06FF\s]+$/;
      if (
        !alphaNumericRegex.test(this.data.diseases) ||
        !alphaNumericRegex.test(this.data.medications) ||
        !alphaNumericRegex.test(this.data.blood_type)
      ) {
        this.$message.error(this.$t("MESSAGES.medicalAlphaNumericOnly"));
        return false;
      }

      const numericRegex = /^[0-9]+$/;
      if (
        !numericRegex.test(this.data.weight) ||
        !numericRegex.test(this.data.height)
      ) {
        this.$message.error(this.$t("MESSAGES.medicalNumericOnly"));
        return false;
      }

      if (
        this.data.diseases.length > 50 ||
        this.data.medications.length > 50 ||
        this.data.blood_type.length > 50 ||
        this.data.weight.length > 50 ||
        this.data.height.length > 50
      ) {
        this.$message.error(this.$t("MESSAGES.medicalMaxLength50"));
        return false;
      }

      return true;
    },

    calculateAge() {
      if (this.data.birth_date) {
        const birthDate = new Date(this.data.birth_date);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        this.data.age = age.toString();
      } else {
        this.data.age = null;
      }
    },

    // Start:: Get Active Jobs
    async getActiveJobs() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "jobs",
          params: {
            page: 0,
            limit: 0,
            is_active: 1,
          },
        });
        this.activeJobs = res.data.data.data?.map((job) => ({
          id: job.id,
          name: job.name,
          value: job.id,
        }));
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Active Jobs

    // Start:: Get Active Banks
    async getActiveBanks() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "banks?page=0&limit=0&is_active=1&ignorePermissionCheck=true",
        });
        const banksList = res.data.data?.data || res.data.data || [];
        this.activeBanks = banksList?.map((bank) => ({
          id: bank.id,
          name: bank.name || bank.title || bank.bank_name,
          value: bank.id,
        }));
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Active Banks

    // Start:: validate Form Inputs
    validateFormInputs() {
      if (!this.data.id_number || this.data.id_number.length !== 10) {
        this.$message.error(this.$t("MESSAGES.idNumberMustBe10Digits"));
        return;
      }

      if (!this.data.mobile) {
        this.$message.error(this.$t("MESSAGES.phoneMustBeEntered"));

      if (!this.data.bank?.value || !this.data.account_number || !this.data.iban) {
        this.$message.error(this.$t("MESSAGES.bankingFieldsRequired"));
        return false;
      }
        return;
      }

      const phoneRegex = /^[0-9]{8,15}$/;
      if (!phoneRegex.test(this.data.mobile)) {
        this.$message.error(this.$t("MESSAGES.phoneMustBeValid"));
        return;
      }

      if (!this.validateMedicalInputs()) {
        return;
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();

      if (this.data.avatar.file) {
        REQUEST_DATA.append("avatar", this.data.avatar.file);
      }
      if (this.data.id_image.file) {
        REQUEST_DATA.append("identity_image", this.data.id_image.file);
      }
      REQUEST_DATA.append("name", this.data.name);
      REQUEST_DATA.append("user_info[job_id]", this.data.job?.value);
      REQUEST_DATA.append("user_info[bank_id]", this.data.bank?.value);
      REQUEST_DATA.append("mobile", this.data.mobile);
      REQUEST_DATA.append("iso_code", this.data.iso_code);
      REQUEST_DATA.append("country_code", "+" + this.data.dial_code);
      REQUEST_DATA.append("user_info[identity_number]", this.data.id_number);
      REQUEST_DATA.append("user_info[birth_date]", this.data.birth_date);
      REQUEST_DATA.append("user_info[diseases]", this.data.diseases);
      REQUEST_DATA.append("user_info[medications]", this.data.medications);
      REQUEST_DATA.append("user_info[blood_type]", this.data.blood_type);
      REQUEST_DATA.append("user_info[weight]", this.data.weight);
      REQUEST_DATA.append("user_info[height]", this.data.height);
      REQUEST_DATA.append("user_info[account_number]", this.data.account_number);
      REQUEST_DATA.append("user_info[iban]", this.data.iban);

      try {
        await this.$axios({
          method: "POST",
          url: `clients`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push({ path: "/employees/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Submit Form
  },

  async created() {
    await this.getActiveJobs();
    await this.getActiveBanks();
  },
};
</script>
